import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Lock, VolumeX, ChevronLeft, ChevronRight, Pause, Play as PlayIcon } from "lucide-react";

const easeOut = [0.16, 1, 0.3, 1];
const AUTO_ADVANCE_MS = 7000;

function extractVideoId(video) {
    if (video?.video_id) return video.video_id;
    const m = video?.thumb?.match(/\/vi\/([^/]+)\//);
    return m ? m[1] : null;
}

export default function FeaturedFlights({ videos = [], onLockClick, weekLabel = "" }) {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hoverPreview, setHoverPreview] = useState(false);
    const [open, setOpen] = useState(false);
    const hoverTimer = useRef(null);
    const total = videos.length;

    const current = videos[index];
    const videoId = extractVideoId(current);
    const locked = current?.locked;
    const canHoverPreview = !locked && !!videoId;

    // Auto-advance
    useEffect(() => {
        if (total <= 1 || paused || open || hoverPreview) return;
        const id = setTimeout(() => setIndex((i) => (i + 1) % total), AUTO_ADVANCE_MS);
        return () => clearTimeout(id);
    }, [index, total, paused, open, hoverPreview]);

    // Reset preview when the slide changes
    useEffect(() => { setHoverPreview(false); clearTimeout(hoverTimer.current); }, [index]);

    if (total === 0 || !current) return null;

    const go = (dir) => setIndex((i) => (i + dir + total) % total);

    const handleEnter = () => {
        if (!canHoverPreview) return;
        clearTimeout(hoverTimer.current);
        hoverTimer.current = setTimeout(() => setHoverPreview(true), 500);
    };
    const handleLeave = () => { clearTimeout(hoverTimer.current); setHoverPreview(false); };

    const handleActivate = () => {
        if (locked) onLockClick?.(current);
        else setOpen(true);
    };

    return (
        <>
            <motion.section
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.9, ease: easeOut }}
                className="relative w-full overflow-hidden border border-[var(--sb-border)] group mb-10 lg:mb-14"
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                data-testid="featured-flights-carousel"
            >
                <div className="relative aspect-[21/9]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={current.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.7, ease: easeOut }}
                            className="absolute inset-0 cursor-pointer"
                            onClick={handleActivate}
                        >
                            <img
                                src={current.thumb}
                                alt={current.title}
                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${hoverPreview ? "opacity-0" : "opacity-100 group-hover:scale-[1.02]"}`}
                                loading="eager"
                            />
                            {hoverPreview && videoId && (
                                <iframe
                                    key={videoId}
                                    src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1&playsinline=1&rel=0`}
                                    title={current.title}
                                    className="absolute inset-0 w-full h-full pointer-events-none"
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media; picture-in-picture"
                                    data-testid="featured-flight-preview"
                                />
                            )}
                            <div className={`absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)] via-[var(--sb-bg)]/55 to-[var(--sb-bg)]/20 transition-opacity duration-700 ${hoverPreview ? "opacity-70" : "opacity-100"}`} />
                            <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-[var(--sb-bg)]/85 via-[var(--sb-bg)]/30 to-transparent" />
                        </motion.div>
                    </AnimatePresence>

                    {locked && (
                        <div className="absolute inset-0 flex items-center justify-center bg-[var(--sb-bg)]/50 backdrop-blur-sm z-10">
                            <div className="flex flex-col items-center gap-3 text-center px-6">
                                <Lock size={30} className="text-[var(--sb-brand)]" />
                                <div className="overline">{current.lock_reason === "login_required" ? "Free with account" : "Subscriber only"}</div>
                            </div>
                        </div>
                    )}

                    {/* Top-left: featured pill + rotation */}
                    <div className="absolute top-5 left-5 lg:top-8 lg:left-8 flex items-center gap-3 z-20 flex-wrap">
                        <span className="flex items-center gap-3 text-[10px] tracking-[0.32em] uppercase text-[var(--sb-text)] bg-[var(--sb-bg)]/40 backdrop-blur-sm border border-white/15 px-3 py-1.5">
                            <span className="w-2 h-2 rounded-full bg-[var(--sb-brand)] animate-pulse" />
                            Featured Flights
                        </span>
                        {weekLabel && total > 1 && (
                            <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--sb-brand)] bg-[var(--sb-bg)]/40 backdrop-blur-sm border border-[var(--sb-brand)]/40 px-3 py-1.5">
                                {weekLabel} · Rotating
                            </span>
                        )}
                    </div>

                    {hoverPreview && !locked && (
                        <div className="absolute top-5 right-5 lg:top-8 lg:right-8 z-20 flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[var(--sb-text)] bg-[var(--sb-bg)]/50 backdrop-blur-sm border border-white/15 px-3 py-1.5">
                            <VolumeX size={11} /> Preview · muted
                        </div>
                    )}

                    {!locked && (
                        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 ${hoverPreview ? "opacity-0" : "opacity-0 group-hover:opacity-100"}`}>
                            <div className="w-20 h-20 rounded-full bg-[var(--sb-brand)] flex items-center justify-center shadow-[0_10px_50px_rgba(107,167,222,0.55)]">
                                <Play size={30} className="text-[var(--sb-bg)] ml-1" fill="currentColor" />
                            </div>
                        </div>
                    )}

                    {/* Bottom: title, region, duration + short description */}
                    <div className="absolute left-0 right-0 bottom-0 p-6 lg:p-10 z-10">
                        <div className="max-w-3xl">
                            <div className="overline mb-3">{current.region}{current.duration ? ` · ${current.duration}` : ""}</div>
                            <h2 className="font-serif text-[var(--sb-text)] leading-[0.98] tracking-tight mb-4" style={{ fontSize: "clamp(1.8rem, 4.5vw, 4rem)" }}>
                                {current.title}
                            </h2>
                            {current.summary && (
                                <p className="text-[var(--sb-text-dim)] text-base lg:text-lg leading-relaxed max-w-2xl">
                                    {current.summary}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Prev / next arrows */}
                    {total > 1 && (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); go(-1); }}
                                aria-label="Previous flight"
                                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-[var(--sb-bg)]/50 backdrop-blur-sm border border-white/15 text-[var(--sb-text)] hover:bg-[var(--sb-brand)] hover:text-[var(--sb-bg)] transition-colors"
                                data-testid="featured-prev"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <button
                                onClick={(e) => { e.stopPropagation(); go(1); }}
                                aria-label="Next flight"
                                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-[var(--sb-bg)]/50 backdrop-blur-sm border border-white/15 text-[var(--sb-text)] hover:bg-[var(--sb-brand)] hover:text-[var(--sb-bg)] transition-colors"
                                data-testid="featured-next"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </>
                    )}
                </div>

                {/* Dot indicators + pause */}
                {total > 1 && (
                    <div className="absolute bottom-3 right-4 z-20 flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            {videos.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                                    aria-label={`Go to slide ${i + 1}`}
                                    className={`h-1 rounded-full transition-all ${i === index ? "w-8 bg-[var(--sb-brand)]" : "w-3 bg-white/30 hover:bg-white/60"}`}
                                    data-testid={`featured-dot-${i}`}
                                />
                            ))}
                        </div>
                        <button
                            onClick={(e) => { e.stopPropagation(); setPaused((p) => !p); }}
                            aria-label={paused ? "Resume rotation" : "Pause rotation"}
                            className="p-1.5 text-white/70 hover:text-white transition-colors"
                            data-testid="featured-play-pause"
                        >
                            {paused ? <PlayIcon size={14} fill="currentColor" /> : <Pause size={14} fill="currentColor" />}
                        </button>
                    </div>
                )}
            </motion.section>

            {open && !locked && videoId && (
                <div className="fixed inset-0 z-[60] bg-[var(--sb-bg)]/92 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setOpen(false)}>
                    <button onClick={() => setOpen(false)} className="absolute top-6 right-6 text-[var(--sb-text)] hover:text-[var(--sb-brand)]"><X size={28} /></button>
                    <div className="w-full max-w-5xl aspect-video bg-black" onClick={(e) => e.stopPropagation()}>
                        <iframe
                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                            title={current.title}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}
        </>
    );
}
